import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { ConvoSessionWithNotes, UpdateConvoSessionRequest, ConvoSituationType, ConvoSessionStatus } from '../../types/convo.js';
import type { ProviderInfo } from '../../types/agent.js';
import * as convoApi from '../../services/convo-api.js';
import { navigateTo } from '../../services/router.js';
import './cp-convo-session-notes.js';
import './cp-convo-partner-profile.js';

const STATUS_LABELS: Record<ConvoSessionStatus, string> = {
  prep: 'Vorbereitung',
  active: 'Aktiv',
  completed: 'Abgeschlossen',
  cancelled: 'Abgebrochen',
};

const TYPE_LABELS: Record<ConvoSituationType, string> = {
  negotiation: 'Verhandlung',
  meeting: 'Meeting',
  sales: 'Sales',
  conflict: 'Konflikt',
  relationship: 'Beziehung',
  persuasion: 'Ueberzeugung',
};

const SITUATION_TYPES: ConvoSituationType[] = ['negotiation', 'meeting', 'sales', 'conflict', 'relationship', 'persuasion'];
const STATUSES: ConvoSessionStatus[] = ['prep', 'active', 'completed', 'cancelled'];

@customElement('cp-convo-session-detail')
export class CpConvoSessionDetail extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) session: ConvoSessionWithNotes | null = null;

  @state() private _editing = false;
  @state() private _saving = false;
  @state() private _deleting = false;
  @state() private _confirmDelete = false;
  @state() private _analyzing = false;
  @state() private _error: string | null = null;
  @state() private _editData: UpdateConvoSessionRequest = {};
  @state() private _providers: ProviderInfo[] = [];
  @state() private _modelsForProvider: string[] = [];
  @state() private _selectedProvider = '';
  @state() private _selectedModel = '';

  connectedCallback(): void {
    super.connectedCallback();
    void this._loadProviders();
  }

  private async _loadProviders(): Promise<void> {
    try {
      const res = await fetch('/api/providers', { credentials: 'include' });
      if (res.ok) {
        this._providers = await res.json() as ProviderInfo[];
        const sessionProvider = this.session?.provider || 'anthropic';
        const match = this._providers.find(p => p.id === sessionProvider);
        this._selectedProvider = match ? match.id : (this._providers[0]?.id ?? 'anthropic');
        await this._loadModelsForProvider(this._selectedProvider);
        const sessionModel = this.session?.model;
        if (sessionModel && this._modelsForProvider.includes(sessionModel)) {
          this._selectedModel = sessionModel;
        }
      }
    } catch { /* keep dropdowns empty, analyze still works with defaults */ }
  }

  private async _loadModelsForProvider(providerId: string): Promise<void> {
    try {
      const res = await fetch(`/api/providers/${encodeURIComponent(providerId)}/models`, { credentials: 'include' });
      if (res.ok) {
        this._modelsForProvider = await res.json() as string[];
        if (!this._modelsForProvider.includes(this._selectedModel)) {
          this._selectedModel = this._modelsForProvider[0] ?? '';
        }
      }
    } catch {
      this._modelsForProvider = [];
    }
  }

  private async _onProviderChange(e: Event): Promise<void> {
    this._selectedProvider = (e.target as HTMLSelectElement).value;
    await this._loadModelsForProvider(this._selectedProvider);
  }

  private _onModelChange(e: Event): void {
    this._selectedModel = (e.target as HTMLSelectElement).value;
  }

  private _startEdit(): void {
    if (!this.session) return;
    this._editData = {
      title: this.session.title,
      description: this.session.description ?? '',
      situation_type: this.session.situation_type,
      status: this.session.status,
      primary_goal: this.session.primary_goal ?? '',
    };
    this._editing = true;
  }

  private _cancelEdit(): void {
    this._editing = false;
    this._error = null;
  }

  private async _saveEdit(): Promise<void> {
    if (!this.session) return;
    this._saving = true;
    this._error = null;
    try {
      await convoApi.updateSession(this.session.id, this._editData);
      this._editing = false;
      this._dispatchUpdated();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Speichern';
    } finally {
      this._saving = false;
    }
  }

  private async _handleDelete(): Promise<void> {
    if (!this.session) return;
    if (!this._confirmDelete) {
      this._confirmDelete = true;
      return;
    }
    this._deleting = true;
    try {
      await convoApi.deleteSession(this.session.id);
      this.dispatchEvent(new CustomEvent('session-deleted', { bubbles: true, composed: true }));
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Loeschen';
      this._confirmDelete = false;
    } finally {
      this._deleting = false;
    }
  }

  private async _handleAnalyze(): Promise<void> {
    if (!this.session || this._analyzing) return;
    this._analyzing = true;
    this._error = null;
    try {
      const opts: { provider?: string; model?: string } = {};
      if (this._selectedProvider) opts.provider = this._selectedProvider;
      if (this._selectedModel) opts.model = this._selectedModel;
      await convoApi.analyzeSession(this.session.id, Object.keys(opts).length ? opts : undefined);
      this._dispatchUpdated();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Analyse fehlgeschlagen';
    } finally {
      this._analyzing = false;
    }
  }

  private _handleBack(): void {
    navigateTo('/conversations');
  }

  private _dispatchUpdated(): void {
    this.dispatchEvent(new CustomEvent('session-updated', { bubbles: true, composed: true }));
  }

  private _handleNoteAdded(): void {
    this._dispatchUpdated();
  }

  private _formatDate(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  private _parseJson(str: string | null): Record<string, unknown> | string[] | null {
    if (!str) return null;
    try { return JSON.parse(str); } catch { return null; }
  }

  render() {
    if (!this.session) {
      return html`<div class="cp-convo-detail"><p>Lade Session...</p></div>`;
    }

    return html`
      <div class="cp-convo-detail">
        <div class="cp-convo-detail__nav">
          <button class="cp-convo-detail__back" @click=${this._handleBack}>
            &#8592; Zurueck
          </button>
        </div>

        ${this._editing ? this._renderEditView() : this._renderReadView()}

        ${this._error ? html`<div class="cp-convo-detail__error">${this._error}</div>` : nothing}

        <cp-convo-session-notes
          .notes=${this.session.notes}
          .sessionId=${this.session.id}
          @note-added=${this._handleNoteAdded}
        ></cp-convo-session-notes>
      </div>
    `;
  }

  private _renderReadView() {
    const s = this.session!;
    const secondaryGoals = this._parseJson(s.secondary_goals) as string[] | null;
    const strategy = this._parseJson(s.strategy) as Record<string, unknown> | null;
    const preparation = this._parseJson(s.preparation) as Record<string, unknown> | null;
    const outcome = this._parseJson(s.outcome) as Record<string, unknown> | null;
    const hasAnalysis = !!strategy || !!preparation;

    return html`
      <div class="cp-convo-detail__header">
        <div class="cp-convo-detail__header-top">
          <div class="cp-convo-detail__meta">
            <span class="cp-convo-card__type-badge cp-convo-card__type-badge--${s.situation_type}">
              ${TYPE_LABELS[s.situation_type]}
            </span>
            <span class="cp-convo-card__status cp-convo-card__status--${s.status}">
              ${STATUS_LABELS[s.status]}
            </span>
          </div>
          <div class="cp-convo-detail__actions">
            <button class="cp-convo-detail__btn cp-convo-detail__btn--edit" @click=${this._startEdit}>Bearbeiten</button>
            <button
              class="cp-convo-detail__btn cp-convo-detail__btn--delete"
              @click=${this._handleDelete}
              ?disabled=${this._deleting}
            >${this._confirmDelete ? 'Wirklich loeschen?' : 'Loeschen'}</button>
          </div>
        </div>
        <h1 class="cp-convo-detail__title">${s.title}</h1>
        ${s.description ? html`<p class="cp-convo-detail__desc">${s.description}</p>` : nothing}
        <div class="cp-convo-detail__date">Erstellt: ${this._formatDate(s.created_at)} | Aktualisiert: ${this._formatDate(s.updated_at)}</div>
      </div>

      ${s.partner ? html`
        <div class="cp-convo-detail__section">
          <h3 class="cp-convo-detail__section-title">Partner</h3>
          <div class="cp-convo-detail__partner"
            @click=${() => navigateTo(`/people/${s.partner!.id}`)}
            role="button" tabindex="0"
          >
            <strong>${s.partner.full_name}</strong>
            ${s.partner.subtitle ? html` <span class="cp-convo-detail__partner-sub">${s.partner.subtitle}</span>` : nothing}
          </div>
        </div>
      ` : nothing}

      ${s.primary_goal ? html`
        <div class="cp-convo-detail__section">
          <h3 class="cp-convo-detail__section-title">Primaerziel</h3>
          <p class="cp-convo-detail__section-content">${s.primary_goal}</p>
        </div>
      ` : nothing}

      ${secondaryGoals && secondaryGoals.length > 0 ? html`
        <div class="cp-convo-detail__section">
          <h3 class="cp-convo-detail__section-title">Sekundaerziele</h3>
          <ul class="cp-convo-detail__goals-list">
            ${secondaryGoals.map(g => html`<li>${g}</li>`)}
          </ul>
        </div>
      ` : nothing}

      ${!hasAnalysis ? html`
        <div class="cp-convo-detail__analyze-bar">
          <p class="cp-convo-detail__analyze-hint">
            ${this._analyzing
              ? 'Analyse laeuft... Partner wird geprueft, Strategie wird generiert'
              : 'Erstelle eine Strategie mit spieltheoretischer Analyse und Antwort-Entwurf'}
          </p>
          ${this._renderProviderModelSelects()}
          <button
            class="cp-convo-detail__btn cp-convo-detail__btn--analyze"
            @click=${this._handleAnalyze}
            ?disabled=${this._analyzing}
          >${this._analyzing ? 'Analysiere...' : 'Analyse starten'}</button>
        </div>
      ` : nothing}

      ${strategy ? this._renderStrategy(strategy) : nothing}
      ${preparation ? this._renderPreparation(preparation) : nothing}

      ${hasAnalysis ? html`
        <div class="cp-convo-detail__analyze-bar">
          <p class="cp-convo-detail__analyze-hint">Analyse erneut durchfuehren</p>
          ${this._renderProviderModelSelects()}
          <button
            class="cp-convo-detail__btn cp-convo-detail__btn--analyze"
            @click=${this._handleAnalyze}
            ?disabled=${this._analyzing}
          >${this._analyzing ? 'Analysiere...' : 'Erneut analysieren'}</button>
        </div>
      ` : nothing}

      ${s.partner_id ? html`
        <cp-convo-partner-profile .personId=${s.partner_id}></cp-convo-partner-profile>
      ` : nothing}

      ${outcome ? html`
        <div class="cp-convo-detail__section">
          <h3 class="cp-convo-detail__section-title">Ergebnis</h3>
          <pre class="cp-convo-detail__json">${JSON.stringify(outcome, null, 2)}</pre>
        </div>
      ` : nothing}
    `;
  }

  private _renderProviderModelSelects() {
    if (this._providers.length === 0) return nothing;
    return html`
      <div class="cp-convo-detail__provider-selects">
        <select
          class="cp-convo-detail__provider-select"
          .value=${this._selectedProvider}
          @change=${(e: Event) => void this._onProviderChange(e)}
          ?disabled=${this._analyzing}
        >
          ${this._providers.map(p => html`
            <option value=${p.id} ?selected=${this._selectedProvider === p.id}>${p.displayName}</option>
          `)}
        </select>
        ${this._modelsForProvider.length > 0 ? html`
          <select
            class="cp-convo-detail__provider-select"
            .value=${this._selectedModel}
            @change=${(e: Event) => this._onModelChange(e)}
            ?disabled=${this._analyzing}
          >
            ${this._modelsForProvider.map(m => html`
              <option value=${m} ?selected=${this._selectedModel === m}>${m}</option>
            `)}
          </select>
        ` : nothing}
      </div>
    `;
  }

  private _renderStrategy(strategy: Record<string, unknown>) {
    const s = strategy;
    return html`
      <div class="cp-convo-detail__section">
        <h3 class="cp-convo-detail__section-title">Strategie</h3>
        <div class="cp-convo-detail__strategy-grid">
          ${s.situation_analysis ? html`
            <div class="cp-convo-detail__strategy-card">
              <div class="cp-convo-detail__strategy-card-title">Situationsanalyse</div>
              <p class="cp-convo-detail__strategy-text">${s.situation_analysis}</p>
            </div>
          ` : nothing}

          ${s.power_dynamics ? html`
            <div class="cp-convo-detail__strategy-card">
              <div class="cp-convo-detail__strategy-card-title">Machtdynamik</div>
              <p class="cp-convo-detail__strategy-text">${s.power_dynamics}</p>
            </div>
          ` : nothing}

          ${s.batna ? html`
            <div class="cp-convo-detail__strategy-card">
              <div class="cp-convo-detail__strategy-card-title">BATNA</div>
              <p class="cp-convo-detail__strategy-text">${s.batna}</p>
            </div>
          ` : nothing}

          ${s.recommended_approach ? html`
            <div class="cp-convo-detail__strategy-card">
              <div class="cp-convo-detail__strategy-card-title">Empfohlene Vorgehensweise</div>
              <p class="cp-convo-detail__strategy-text">${s.recommended_approach}</p>
            </div>
          ` : nothing}

          ${Array.isArray(s.cialdini_principles) && s.cialdini_principles.length > 0 ? html`
            <div class="cp-convo-detail__strategy-card">
              <div class="cp-convo-detail__strategy-card-title">Cialdini-Prinzipien</div>
              <ul class="cp-convo-detail__strategy-list">
                ${(s.cialdini_principles as string[]).map(p => html`<li>${p}</li>`)}
              </ul>
            </div>
          ` : nothing}

          ${Array.isArray(s.key_arguments) && s.key_arguments.length > 0 ? html`
            <div class="cp-convo-detail__strategy-card">
              <div class="cp-convo-detail__strategy-card-title">Schluesselargumente</div>
              <ul class="cp-convo-detail__strategy-list">
                ${(s.key_arguments as string[]).map(a => html`<li>${a}</li>`)}
              </ul>
            </div>
          ` : nothing}

          ${Array.isArray(s.risks) && s.risks.length > 0 ? html`
            <div class="cp-convo-detail__strategy-card">
              <div class="cp-convo-detail__strategy-card-title">Risiken</div>
              <ul class="cp-convo-detail__strategy-list">
                ${(s.risks as string[]).map(r => html`<li>${r}</li>`)}
              </ul>
            </div>
          ` : nothing}
        </div>
      </div>
    `;
  }

  private _renderPreparation(preparation: Record<string, unknown>) {
    const p = preparation;
    return html`
      <div class="cp-convo-detail__section">
        <h3 class="cp-convo-detail__section-title">Vorbereitung</h3>
        <div class="cp-convo-detail__strategy-grid">
          ${p.opening_strategy ? html`
            <div class="cp-convo-detail__strategy-card">
              <div class="cp-convo-detail__strategy-card-title">Eroeffnungsstrategie</div>
              <p class="cp-convo-detail__strategy-text">${p.opening_strategy}</p>
            </div>
          ` : nothing}

          ${p.tone_guide ? html`
            <div class="cp-convo-detail__strategy-card">
              <div class="cp-convo-detail__strategy-card-title">Tonalitaet</div>
              <p class="cp-convo-detail__strategy-text">${p.tone_guide}</p>
            </div>
          ` : nothing}

          ${Array.isArray(p.key_questions) && p.key_questions.length > 0 ? html`
            <div class="cp-convo-detail__strategy-card">
              <div class="cp-convo-detail__strategy-card-title">Schluesselfragen</div>
              <ul class="cp-convo-detail__strategy-list">
                ${(p.key_questions as string[]).map(q => html`<li>${q}</li>`)}
              </ul>
            </div>
          ` : nothing}

          ${Array.isArray(p.boundaries) && p.boundaries.length > 0 ? html`
            <div class="cp-convo-detail__strategy-card">
              <div class="cp-convo-detail__strategy-card-title">Grenzen / Deal-Breaker</div>
              <ul class="cp-convo-detail__strategy-list">
                ${(p.boundaries as string[]).map(b => html`<li>${b}</li>`)}
              </ul>
            </div>
          ` : nothing}

          ${p.draft_response ? html`
            <div class="cp-convo-detail__strategy-card">
              <div class="cp-convo-detail__strategy-card-title">Antwort-Entwurf</div>
              <div class="cp-convo-detail__draft">${p.draft_response}</div>
            </div>
          ` : nothing}
        </div>
      </div>
    `;
  }

  private _renderEditView() {
    return html`
      <div class="cp-convo-detail__edit">
        <div class="cp-convo-detail__field">
          <label class="cp-convo-detail__label">Titel</label>
          <input
            class="cp-convo-detail__input"
            type="text"
            .value=${this._editData.title ?? ''}
            @input=${(e: Event) => { this._editData = { ...this._editData, title: (e.target as HTMLInputElement).value }; }}
            ?disabled=${this._saving}
          />
        </div>

        <div class="cp-convo-detail__field">
          <label class="cp-convo-detail__label">Beschreibung</label>
          <textarea
            class="cp-convo-detail__textarea"
            rows="3"
            .value=${this._editData.description ?? ''}
            @input=${(e: Event) => { this._editData = { ...this._editData, description: (e.target as HTMLTextAreaElement).value }; }}
            ?disabled=${this._saving}
          ></textarea>
        </div>

        <div class="cp-convo-detail__row">
          <div class="cp-convo-detail__field">
            <label class="cp-convo-detail__label">Typ</label>
            <select
              class="cp-convo-detail__select"
              @change=${(e: Event) => { this._editData = { ...this._editData, situation_type: (e.target as HTMLSelectElement).value as ConvoSituationType }; }}
              ?disabled=${this._saving}
            >
              ${SITUATION_TYPES.map(t => html`
                <option value=${t} ?selected=${this._editData.situation_type === t}>${TYPE_LABELS[t]}</option>
              `)}
            </select>
          </div>

          <div class="cp-convo-detail__field">
            <label class="cp-convo-detail__label">Status</label>
            <select
              class="cp-convo-detail__select"
              @change=${(e: Event) => { this._editData = { ...this._editData, status: (e.target as HTMLSelectElement).value as ConvoSessionStatus }; }}
              ?disabled=${this._saving}
            >
              ${STATUSES.map(s => html`
                <option value=${s} ?selected=${this._editData.status === s}>${STATUS_LABELS[s]}</option>
              `)}
            </select>
          </div>
        </div>

        <div class="cp-convo-detail__field">
          <label class="cp-convo-detail__label">Primaerziel</label>
          <input
            class="cp-convo-detail__input"
            type="text"
            .value=${this._editData.primary_goal ?? ''}
            @input=${(e: Event) => { this._editData = { ...this._editData, primary_goal: (e.target as HTMLInputElement).value }; }}
            ?disabled=${this._saving}
          />
        </div>

        <div class="cp-convo-detail__edit-actions">
          <button class="cp-convo-detail__btn cp-convo-detail__btn--cancel" @click=${this._cancelEdit} ?disabled=${this._saving}>
            Abbrechen
          </button>
          <button class="cp-convo-detail__btn cp-convo-detail__btn--save" @click=${this._saveEdit} ?disabled=${this._saving}>
            ${this._saving ? 'Wird gespeichert...' : 'Speichern'}
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-convo-session-detail': CpConvoSessionDetail;
  }
}
